import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Bot, Plus, Play, Pause, Trash2, Settings, MapPin, Mail,
  Users, Target, Zap, TrendingUp, ArrowUpRight, ChevronLeft,
  ChevronRight, Clock, CheckCircle2, XCircle, Search, Loader2,
  BarChart3, Building2, Briefcase, Globe, Copy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  active: 'bg-emerald-500/20 text-emerald-400',
  paused: 'bg-amber-500/20 text-amber-400',
  stopped: 'bg-red-500/20 text-red-400',
};

const prospectStatusColors = {
  discovered: 'bg-blue-500/20 text-blue-400',
  lead_created: 'bg-indigo-500/20 text-indigo-400',
  contacted: 'bg-emerald-500/20 text-emerald-400',
  sequenced: 'bg-purple-500/20 text-purple-400',
  escalated: 'bg-amber-500/20 text-amber-400',
  disqualified: 'bg-red-500/20 text-red-400',
};

const activityIcons = {
  prospect: Search,
  qualify: CheckCircle2,
  email_sent: Mail,
  sequence_created: Zap,
  escalated: ArrowUpRight,
  error: XCircle,
};

export default function AdminAIAgents() {
  const { token } = useAuth();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState({});
  const [detailTab, setDetailTab] = useState('overview');
  const [agentEmails, setAgentEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [newAgent, setNewAgent] = useState({
    name: '', territories: [], daily_email_limit: 20,
    auto_sequence_mode: 'auto', escalation_score_threshold: 70, custom_instructions: ''
  });
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAgents = useCallback(async () => {
    try {
      const [agentsRes, presetsRes] = await Promise.all([
        axios.get(`${API}/admin/ai-agents`, { headers }),
        axios.get(`${API}/admin/ai-agents/territory-presets`, { headers })
      ]);
      setAgents(agentsRes.data);
      setPresets(presetsRes.data);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const fetchAgentDetail = async (agentId) => {
    try {
      const res = await axios.get(`${API}/admin/ai-agents/${agentId}`, { headers });
      setSelectedAgent(res.data);
    } catch (err) {
      toast.error('Failed to load agent details');
    }
  };

  const fetchAgentEmails = async (agentId) => {
    setEmailsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-agents/${agentId}/emails`, { headers });
      setAgentEmails(res.data);
    } catch (err) {
      console.error('Failed to fetch emails:', err);
      setAgentEmails([]);
    } finally {
      setEmailsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAgent.name) { toast.error('Name required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/ai-agents`, newAgent, { headers });
      toast.success('AI Agent created!');
      setShowCreate(false);
      setNewAgent({ name: '', territories: [], daily_email_limit: 20, auto_sequence_mode: 'auto', escalation_score_threshold: 70, custom_instructions: '' });
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create agent');
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (agentId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/admin/ai-agents/${agentId}`, { status: newStatus }, { headers });
      toast.success(`Agent ${newStatus}`);
      fetchAgents();
      if (selectedAgent?.agent_id === agentId) fetchAgentDetail(agentId);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const deleteAgent = async (agentId) => {
    try {
      await axios.delete(`${API}/admin/ai-agents/${agentId}`, { headers });
      toast.success('Agent deleted');
      setSelectedAgent(null);
      fetchAgents();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const runNow = async (agentId) => {
    setRunning(prev => ({ ...prev, [agentId]: true }));
    try {
      const res = await axios.post(`${API}/admin/ai-agents/${agentId}/run-now`, {}, { headers });
      const r = res.data.results;
      toast.success(`Pipeline: ${r.prospects} prospects, ${r.leads_created} leads, ${r.emails_sent} emails, ${r.sequences_created} sequences, ${r.escalated} escalated`);
      fetchAgents();
      if (selectedAgent?.agent_id === agentId) fetchAgentDetail(agentId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pipeline failed');
    } finally {
      setRunning(prev => ({ ...prev, [agentId]: false }));
    }
  };

  const toggleTerritory = (key) => {
    setNewAgent(prev => ({
      ...prev,
      territories: prev.territories.includes(key)
        ? prev.territories.filter(t => t !== key)
        : [...prev.territories, key]
    }));
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-muted-foreground">Loading AI Agents...</div></div>;

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="admin-ai-agents">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3" data-testid="page-title">
              <Bot className="h-8 w-8 text-indigo-400" /> AI Sales Agents
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Autonomous prospecting, outreach, and lead nurturing</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-ai-agent-btn">
            <Plus className="h-4 w-4 mr-1" /> New AI Agent
          </Button>
        </div>

        {selectedAgent ? (
          /* ===== AGENT DETAIL VIEW ===== */
          <div data-testid="agent-detail">
            <Button variant="ghost" size="sm" onClick={() => setSelectedAgent(null)} className="text-xs text-muted-foreground mb-4" data-testid="back-to-list">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to agents
            </Button>

            {/* Agent Header */}
            <Card className="bg-muted/60 border-border mb-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                        <Bot className="h-5 w-5 text-indigo-400" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-foreground">{selectedAgent.name}</h2>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={statusColors[selectedAgent.status]}>{selectedAgent.status}</Badge>
                          <span className="text-xs text-muted-foreground">{(selectedAgent.territory_names || []).join(', ')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => runNow(selectedAgent.agent_id)} disabled={running[selectedAgent.agent_id]} data-testid="run-now-btn">
                      {running[selectedAgent.agent_id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Zap className="h-3 w-3 mr-1 text-amber-400" />}
                      Run Pipeline Now
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => toggleStatus(selectedAgent.agent_id, selectedAgent.status)}>
                      {selectedAgent.status === 'active' ? <><Pause className="h-3 w-3 mr-1" /> Pause</> : <><Play className="h-3 w-3 mr-1" /> Resume</>}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-red-400" onClick={() => deleteAgent(selectedAgent.agent_id)}>
                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="agent-stats">
              {[
                { label: 'Prospects', value: selectedAgent.stats?.total_prospects || 0, color: 'text-blue-400', icon: Search },
                { label: 'Leads', value: selectedAgent.stats?.total_leads_created || 0, color: 'text-indigo-400', icon: Users },
                { label: 'Emails Sent', value: selectedAgent.stats?.total_emails_sent || 0, color: 'text-emerald-400', icon: Mail },
                { label: 'Sequences', value: selectedAgent.stats?.total_sequences_created || 0, color: 'text-purple-400', icon: Zap },
                { label: 'Escalated', value: selectedAgent.stats?.total_escalated || 0, color: 'text-amber-400', icon: ArrowUpRight },
                { label: 'Today', value: selectedAgent.stats?.emails_sent_today || 0, sub: `/ ${selectedAgent.daily_email_limit}`, color: 'text-foreground', icon: Clock },
              ].map(s => (
                <Card key={s.label} className="bg-muted/60 border-border">
                  <CardContent className="p-3">
                    <div className={`flex items-center gap-1.5 text-[10px] ${s.color} mb-0.5`}>
                      <s.icon className="h-3 w-3" /> {s.label}
                    </div>
                    <p className={`text-xl font-bold ${s.color}`}>
                      {s.value}{s.sub && <span className="text-xs text-muted-foreground">{s.sub}</span>}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Detail Tabs */}
            <Tabs value={detailTab} onValueChange={setDetailTab}>
              <TabsList className="bg-card border-border mb-4">
                <TabsTrigger value="overview">Config</TabsTrigger>
                <TabsTrigger value="prospects" data-testid="tab-prospects">Prospects</TabsTrigger>
                <TabsTrigger value="emails" data-testid="tab-emails" onClick={() => { if (agentEmails.length === 0) fetchAgentEmails(selectedAgent.agent_id); }}>
                  Emails ({selectedAgent.stats?.total_emails_sent || 0})
                </TabsTrigger>
                <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="bg-muted/60 border-border">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground flex items-center gap-2"><MapPin className="h-4 w-4 text-indigo-400" /> Territories</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {(selectedAgent.territory_names || []).map(t => (
                          <Badge key={t} className="bg-indigo-500/20 text-indigo-400 text-xs">{t}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{(selectedAgent.all_zip_codes || []).length} zip codes covered</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(selectedAgent.all_zip_codes || []).map(z => (
                          <span key={z} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{z}</span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/60 border-border">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground flex items-center gap-2"><Target className="h-4 w-4 text-amber-400" /> Targeting</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1.5">
                        {(selectedAgent.target_types || []).map(t => (
                          <Badge key={t} variant="outline" className="text-[10px] capitalize">{t.replace(/_/g, ' ')}</Badge>
                        ))}
                      </div>
                      <div className="mt-3 space-y-1.5 text-xs">
                        <div className="flex justify-between"><span className="text-muted-foreground">Daily limit:</span><span className="text-foreground">{selectedAgent.daily_email_limit} emails</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Sequence mode:</span><span className="text-foreground capitalize">{selectedAgent.auto_sequence_mode}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Escalation threshold:</span><span className="text-foreground">{selectedAgent.escalation_score_threshold}+ score</span></div>
                      </div>
                      {selectedAgent.custom_instructions && (
                        <div className="mt-3 p-2 rounded bg-muted/50">
                          <p className="text-[10px] text-muted-foreground font-semibold mb-0.5">Custom Instructions</p>
                          <p className="text-xs text-muted-foreground">{selectedAgent.custom_instructions}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="prospects">
                <Card className="bg-muted/60 border-border">
                  <CardContent className="p-0" data-testid="prospects-table">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground text-xs">Business</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Type</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Address</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Rating</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(selectedAgent.prospects || []).map(p => (
                          <TableRow key={p.prospect_id} className="border-border/50">
                            <TableCell>
                              <div>
                                <p className="text-sm font-medium text-foreground">{p.business_name}</p>
                                {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-400 hover:underline flex items-center gap-0.5"><Globe className="h-2.5 w-2.5" /> Website</a>}
                              </div>
                            </TableCell>
                            <TableCell><Badge variant="outline" className="text-[10px] capitalize">{(p.business_type || '').replace(/_/g, ' ')}</Badge></TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{p.address}</TableCell>
                            <TableCell className="text-xs text-amber-400">{p.rating ? `${p.rating} (${p.review_count})` : '-'}</TableCell>
                            <TableCell><Badge className={`text-[9px] ${prospectStatusColors[p.status] || ''}`}>{p.status}</Badge></TableCell>
                          </TableRow>
                        ))}
                        {(selectedAgent.prospects || []).length === 0 && (
                          <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No prospects yet. Click "Run Pipeline Now" to start prospecting.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="emails">
                <Card className="bg-muted/60 border-border">
                  <CardContent className="p-4" data-testid="emails-log">
                    {emailsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
                        <span className="ml-2 text-muted-foreground">Loading emails...</span>
                      </div>
                    ) : agentEmails.length === 0 ? (
                      <div className="text-center py-12">
                        <Mail className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="text-sm text-muted-foreground">No emails sent yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Click "Run Pipeline Now" to start sending outreach</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {agentEmails.map(email => (
                          <div key={email.outreach_id}
                            className="rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => setExpandedEmail(expandedEmail === email.outreach_id ? null : email.outreach_id)}
                            data-testid={`email-row-${email.outreach_id}`}
                          >
                            <div className="flex items-center gap-3 p-3">
                              <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                                <Mail className="h-4 w-4 text-emerald-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-foreground truncate">{email.subject}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-muted-foreground truncate">{email.client_name}</span>
                                  {email.client_email && (
                                    <span className="text-[10px] text-muted-foreground truncate max-w-[180px]">&lt;{email.client_email}&gt;</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-[10px] text-muted-foreground">{new Date(email.created_at).toLocaleDateString()}</p>
                                <p className="text-[10px] text-muted-foreground">{new Date(email.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</p>
                              </div>
                              <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${expandedEmail === email.outreach_id ? 'rotate-90' : ''}`} />
                            </div>
                            {expandedEmail === email.outreach_id && (
                              <div className="border-t border-border p-4" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center gap-2 mb-3 text-xs">
                                  <span className="text-muted-foreground">To:</span>
                                  <span className="text-muted-foreground">{email.client_name} &lt;{email.client_email}&gt;</span>
                                </div>
                                <div className="flex items-center gap-2 mb-3 text-xs">
                                  <span className="text-muted-foreground">Subject:</span>
                                  <span className="text-foreground font-medium">{email.subject}</span>
                                </div>
                                <div className="bg-muted/60 rounded-lg p-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                                  {email.body_preview}
                                </div>
                                <div className="flex items-center gap-2 mt-3">
                                  {email.client_email && (
                                    <Button size="sm" variant="outline" className="h-7 text-[10px]"
                                      onClick={() => { navigator.clipboard.writeText(email.client_email); toast.success('Email copied'); }}>
                                      <Copy className="h-3 w-3 mr-1" /> Copy Email
                                    </Button>
                                  )}
                                  <Badge className="bg-emerald-500/20 text-emerald-400 text-[9px]">Sent</Badge>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="activity">
                <Card className="bg-muted/60 border-border">
                  <CardContent className="p-4" data-testid="activity-log">
                    <div className="space-y-2">
                      {(selectedAgent.recent_activity || []).map(a => {
                        const Icon = activityIcons[a.action] || Clock;
                        return (
                          <div key={a.activity_id} className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/40">
                            <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Icon className="h-3.5 w-3.5 text-indigo-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[9px] capitalize">{a.action.replace(/_/g, ' ')}</Badge>
                                <span className="text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">{a.details}</p>
                            </div>
                          </div>
                        );
                      })}
                      {(selectedAgent.recent_activity || []).length === 0 && (
                        <p className="text-center text-muted-foreground py-8 text-sm">No activity yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          /* ===== AGENTS LIST VIEW ===== */
          <div className="space-y-3" data-testid="agents-list">
            {agents.map(agent => {
              const stats = agent.stats || {};
              return (
                <Card key={agent.agent_id} className="bg-muted/60 border-border hover:border-border transition-colors cursor-pointer" onClick={() => fetchAgentDetail(agent.agent_id)} data-testid={`agent-${agent.agent_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${agent.status === 'active' ? 'bg-emerald-500/20' : 'bg-muted'}`}>
                          <Bot className={`h-5 w-5 ${agent.status === 'active' ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-foreground">{agent.name}</p>
                            <Badge className={`text-[9px] ${statusColors[agent.status]}`}>{agent.status}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{(agent.territory_names || []).join(', ')} | {(agent.all_zip_codes || []).length} zip codes</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="grid grid-cols-4 gap-4 text-center">
                          {[
                            { label: 'Prospects', val: stats.total_prospects || 0 },
                            { label: 'Leads', val: stats.total_leads_created || 0 },
                            { label: 'Emails', val: stats.total_emails_sent || 0 },
                            { label: 'Escalated', val: stats.total_escalated || 0 },
                          ].map(s => (
                            <div key={s.label}>
                              <p className="text-sm font-bold text-foreground">{s.val}</p>
                              <p className="text-[9px] text-muted-foreground">{s.label}</p>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => runNow(agent.agent_id)} disabled={running[agent.agent_id]}>
                            {running[agent.agent_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 text-amber-400" />}
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => toggleStatus(agent.agent_id, agent.status)}>
                            {agent.status === 'active' ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                          </Button>
                          <ChevronRight className="h-4 w-4 text-muted-foreground ml-2" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {agents.length === 0 && (
              <div className="text-center py-16">
                <Bot className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-1">No AI Agents Yet</h3>
                <p className="text-muted-foreground text-sm mb-4">Create an AI Sales Agent to automatically prospect businesses in your target territories</p>
                <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="h-4 w-4 mr-1" /> Create First AI Agent
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ===== CREATE DIALOG ===== */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-agent-dialog">
            <DialogHeader><DialogTitle className="text-foreground flex items-center gap-2"><Bot className="h-5 w-5 text-indigo-400" /> Create AI Sales Agent</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Agent Name *</Label>
                <Input value={newAgent.name} onChange={e => setNewAgent(p => ({ ...p, name: e.target.value }))} className="bg-muted border-border h-9" placeholder="e.g. Denver AI Closer" data-testid="agent-name-input" />
              </div>

              <div>
                <Label className="text-xs mb-2 block">Territories (click to select)</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="territory-selector">
                  {Object.entries(presets).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => toggleTerritory(key)}
                      className={`p-2.5 rounded-lg border text-left transition-all ${
                        newAgent.territories.includes(key)
                          ? 'bg-indigo-500/20 border-indigo-500/40'
                          : 'bg-muted/50 border-border hover:border-border'
                      }`}
                    >
                      <p className={`text-xs font-semibold ${newAgent.territories.includes(key) ? 'text-indigo-400' : 'text-foreground'}`}>{preset.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{preset.description}</p>
                      <p className="text-[10px] text-muted-foreground">{preset.zip_codes.length} zip codes</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Daily Email Limit</Label>
                  <Input type="number" value={newAgent.daily_email_limit} onChange={e => setNewAgent(p => ({ ...p, daily_email_limit: parseInt(e.target.value) || 20 }))} className="bg-muted border-border h-9" />
                </div>
                <div>
                  <Label className="text-xs">Escalation Score</Label>
                  <Input type="number" value={newAgent.escalation_score_threshold} onChange={e => setNewAgent(p => ({ ...p, escalation_score_threshold: parseInt(e.target.value) || 70 }))} className="bg-muted border-border h-9" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Sequence Mode</Label>
                <Select value={newAgent.auto_sequence_mode} onValueChange={v => setNewAgent(p => ({ ...p, auto_sequence_mode: v }))}>
                  <SelectTrigger className="bg-muted border-border h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Fully Automatic (hands-free outreach)</SelectItem>
                    <SelectItem value="approval">Approval Required (review before send)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Custom Instructions (optional)</Label>
                <Textarea value={newAgent.custom_instructions} onChange={e => setNewAgent(p => ({ ...p, custom_instructions: e.target.value }))} className="bg-muted border-border" rows={3} placeholder="e.g. Focus on high-budget corporate events, luxury weddings, and pharma lunch-and-learns" />
              </div>

              <Button onClick={handleCreate} className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={creating || !newAgent.name || newAgent.territories.length === 0} data-testid="create-agent-submit">
                {creating ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Creating...</> : <><Bot className="h-4 w-4 mr-1" /> Deploy AI Agent</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
